import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Flex, Popover, Tag, Tooltip, Upload, message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { DeleteIcon } from '@/assets/icons/icons';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';

import {
  AssignShipmentAlert,
  fetchMentionUsers,
  GetAlertOwnerOptions,
  type AlertOwnerOptionsData,
} from '@/apis/shipment';
import type { AlertCloseTagGroup, AlertOwnershipSnapshot } from '@/apis/types';
import TeamUserSelect, {
  type AdditionalOption,
  type TeamData,
  type TeamUserSelection,
} from '@/components/TeamUserSelect';
import AddLinkForm from '../partials/AddLinkForm';
import RichEditor, { type RichEditorRef } from '../partials/RichEditor';
import {
  getAlertCloseErrorMessage,
  TAG_REQUIRED_MESSAGE,
} from './alertCloseErrors';
import {
  ALERT_COMMENT_MAX_LENGTH,
  COMMENT_TOO_LONG_MESSAGE,
  getCommentTextLength,
} from './alertCloseComment';
import styles from './AlertAssignmentPanel.module.css';

/** What the panel commits: owners for an alert, or a resolve / dismiss. */
export type AlertPanelMode = 'assign' | 'resolve' | 'dismiss';

export interface CloseAlertValues {
  tagId: string | null;
  comment: string;
}

interface AlertAssignmentPanelProps {
  alertId: string;
  shipmentId: string;
  token: string;
  /** Defaults to assigning owners. */
  mode?: AlertPanelMode;
  /** Owner and tag data; needed when assigning. */
  ownership?: AlertOwnershipSnapshot;
  /** Tags and the required rule for resolve / dismiss. */
  closeTags?: AlertCloseTagGroup;
  /**
   * Commits a resolve / dismiss. The parent owns the request and its success
   * handling; the panel shows the error if it rejects.
   */
  onSubmitClose?: (values: CloseAlertValues) => Promise<void>;
  onCancel: () => void;
  onCommitted: () => void;
  onUnauthorized: (error: unknown) => void;
}

// Floating layers opened from inside the panel (picker, dialogs, @mentions).
const FLOATING_LAYER_SELECTOR =
  '.ant-popover, .ant-modal-root, .mention-dropdown, .tippy-box';

// Stable stand-in so the owner hooks need no branching when resolving / dismissing.
const EMPTY_OWNERSHIP: AlertOwnershipSnapshot = { enabled: false };

const TAG_GROUP_LABEL: Record<AlertPanelMode, string> = {
  assign: 'Assignment tag',
  resolve: 'Resolution tag',
  dismiss: 'Dismissal tag',
};

const PRIMARY_LABEL: Record<Exclude<AlertPanelMode, 'assign'>, string> = {
  resolve: 'Mark as resolved',
  dismiss: 'Dismiss',
};

const footerIcons = {
  attachment: {
    viewBox: '156 241 8 16',
    path: 'M162.667 245V252.667C162.667 254.14 161.473 255.333 160 255.333C158.527 255.333 157.333 254.14 157.333 252.667V244.333C157.333 243.413 158.08 242.667 159 242.667C159.92 242.667 160.667 243.413 160.667 244.333V251.333C160.667 251.7 160.367 252 160 252C159.633 252 159.333 251.7 159.333 251.333V245H158.333V251.333C158.333 252.253 159.08 253 160 253C160.92 253 161.667 252.253 161.667 251.333V244.333C161.667 242.86 160.473 241.667 159 241.667C157.527 241.667 156.333 242.86 156.333 244.333V252.667C156.333 254.693 157.973 256.333 160 256.333C162.027 256.333 163.667 254.693 163.667 252.667V245H162.667Z',
  },
  mention: {
    viewBox: '189 242 14 14',
    path: 'M196 242.333C192.32 242.333 189.333 245.32 189.333 249C189.333 252.68 192.32 255.667 196 255.667H199.333V254.333H196C193.107 254.333 190.667 251.893 190.667 249C190.667 246.107 193.107 243.667 196 243.667C198.893 243.667 201.333 246.107 201.333 249V249.953C201.333 250.48 200.86 251 200.333 251C199.807 251 199.333 250.48 199.333 249.953V249C199.333 247.16 197.84 245.667 196 245.667C194.16 245.667 192.667 247.16 192.667 249C192.667 250.84 194.16 252.333 196 252.333C196.92 252.333 197.76 251.96 198.36 251.353C198.793 251.947 199.54 252.333 200.333 252.333C201.647 252.333 202.667 251.267 202.667 249.953V249C202.667 245.32 199.68 242.333 196 242.333ZM196 251C194.893 251 194 250.107 194 249C194 247.893 194.893 247 196 247C197.107 247 198 247.893 198 249C198 250.107 197.107 251 196 251Z',
  },
  link: {
    viewBox: '225 245 14 8',
    path: 'M226.6 249C226.6 247.86 227.527 246.933 228.667 246.933H231.333V245.667H228.667C226.827 245.667 225.333 247.16 225.333 249C225.333 250.84 226.827 252.333 228.667 252.333H231.333V251.067H228.667C227.527 251.067 226.6 250.14 226.6 249ZM229.333 249.667H234.667V248.333H229.333V249.667ZM235.333 245.667H232.667V246.933H235.333C236.473 246.933 237.4 247.86 237.4 249C237.4 250.14 236.473 251.067 235.333 251.067H232.667V252.333H235.333C237.173 252.333 238.667 250.84 238.667 249C238.667 247.16 237.173 245.667 235.333 245.667Z',
  },
} as const;

type FooterIconName = keyof typeof footerIcons;

const FooterIcon: React.FC<{ name: FooterIconName }> = ({ name }) => {
  const icon = footerIcons[name];
  return (
    <svg
      aria-hidden
      data-icon={name}
      width='16'
      height='16'
      viewBox={icon.viewBox}
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path d={icon.path} fill='currentColor' />
    </svg>
  );
};

interface MentionRecipient {
  id: string | number;
  name: string;
  email?: string;
  initials?: string;
  author_badge_color?: string;
}

function initialSelection(
  ownership: AlertOwnershipSnapshot
): TeamUserSelection {
  const result: TeamUserSelection = { teamIds: [], userIds: [] };
  for (const selection of ownership.assignment?.selections ?? []) {
    if (selection.type === 'team' && selection.id) {
      result.teamIds.push(selection.id);
    } else if (selection.type === 'user' && selection.id) {
      result.userIds.push(selection.id);
    } else if (selection.type === 'role' && selection.key) {
      result.userIds.push(selection.key);
    }
  }
  return result;
}

function initialTagId(ownership: AlertOwnershipSnapshot): string | null {
  const currentTagId = ownership.assignment?.tag?.id;
  return currentTagId && ownership.tags?.some((tag) => tag.id === currentTagId)
    ? currentTagId
    : null;
}

function selectionsMatch(
  current: TeamUserSelection,
  initial: TeamUserSelection
): boolean {
  return (
    current.teamIds.length === initial.teamIds.length &&
    current.userIds.length === initial.userIds.length &&
    current.teamIds.every((id) => initial.teamIds.includes(id)) &&
    current.userIds.every((id) => initial.userIds.includes(id))
  );
}

const AlertAssignmentPanel: React.FC<AlertAssignmentPanelProps> = ({
  alertId,
  shipmentId,
  token,
  mode = 'assign',
  ownership: ownershipProp,
  closeTags,
  onSubmitClose,
  onCancel,
  onCommitted,
  onUnauthorized,
}) => {
  const isAssign = mode === 'assign';
  const ownership = ownershipProp ?? EMPTY_OWNERSHIP;
  const tags = isAssign ? (ownership.tags ?? []) : (closeTags?.tags ?? []);
  const tagRequired = isAssign
    ? Boolean(ownership.require_tag)
    : Boolean(closeTags?.required);
  const [options, setOptions] = useState<AlertOwnerOptionsData | null>(null);
  const [selection, setSelection] = useState<TeamUserSelection>(() =>
    initialSelection(ownership)
  );
  // Assign keeps the current assignment tag; resolve and dismiss start empty.
  const [selectedTagId, setSelectedTagId] = useState<string | null>(() =>
    isAssign ? initialTagId(ownership) : null
  );
  // Resolve / dismiss have no owner list to load.
  const [loading, setLoading] = useState(isAssign);
  // Resolve / dismiss comment, fed by the editor's change callback.
  const [commentLength, setCommentLength] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mentionRecipients, setMentionRecipients] = useState<
    MentionRecipient[]
  >([]);
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const editorRef = useRef<RichEditorRef>(null);

  useEffect(() => {
    if (!isAssign) return undefined;
    let mounted = true;
    setLoading(true);
    GetAlertOwnerOptions(token, shipmentId)
      .then((response) => {
        if (mounted) setOptions(response.data.data.data);
      })
      .catch((error) => {
        message.error('Unable to load alert owners.');
        onUnauthorized(error);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isAssign, onUnauthorized, shipmentId, token]);

  useEffect(() => {
    let mounted = true;
    fetchMentionUsers(token, shipmentId)
      .then((response) => {
        if (!mounted) return;
        setMentionRecipients(
          (response.data.data.data ?? []).map((recipient) => ({
            id: recipient.id,
            name: recipient.name,
            email: recipient.email,
            initials: recipient.badge,
            author_badge_color: recipient.badge_color,
          }))
        );
      })
      .catch(onUnauthorized);
    return () => {
      mounted = false;
    };
  }, [onUnauthorized, shipmentId, token]);

  const searchMentions = useCallback(
    async (query: string): Promise<MentionRecipient[]> => {
      const response = await fetchMentionUsers(
        token,
        shipmentId,
        undefined,
        query
      );
      return (response.data.data.data ?? []).map((recipient) => ({
        id: recipient.id,
        name: recipient.name,
        email: recipient.email,
        initials: recipient.badge,
        author_badge_color: recipient.badge_color,
      }));
    },
    [shipmentId, token]
  );

  const userColorMap = useMemo(
    () =>
      Object.fromEntries(
        mentionRecipients.map((recipient) => [
          recipient.name,
          recipient.author_badge_color ?? '#1677ff',
        ])
      ),
    [mentionRecipients]
  );

  const handleFileChange = (change: UploadChangeParam<UploadFile>) => {
    if (change.file) {
      setSelectedFiles((current) => [...current, change.file]);
    }
  };

  const openLinkModal = () => {
    setLinkText(editorRef.current?.getSelectedText() ?? '');
    setLinkModalOpen(true);
  };

  const teamsData = useMemo<TeamData[]>(() => {
    const teams: TeamData[] = (options?.teams ?? []).map((team) => ({
      teamId: team.id,
      teamName: team.name,
      color: team.color,
      users: team.users.map((user) => ({ ...user, teamId: team.id })),
    }));
    const knownTeamIds = new Set(teams.map((team) => team.teamId));
    for (const current of ownership.assignment?.selections ?? []) {
      if (
        current.type === 'team' &&
        current.id &&
        !knownTeamIds.has(current.id)
      ) {
        teams.push({
          teamId: current.id,
          teamName: `${current.label} (unavailable)`,
          users: [],
        });
      }
    }
    return teams;
  }, [options, ownership.assignment?.selections]);

  const roleOptions = useMemo<AdditionalOption[]>(() => {
    const roles = (options?.roles ?? []).map((role) => {
      const current = ownership.assignment?.selections.find(
        (selection) => selection.type === 'role' && selection.key === role.key
      );
      return {
        id: role.key,
        name: !role.available && current ? current.label : role.display_label,
        disabled: !role.available,
        disabledReason: role.disabled_reason,
      };
    });
    const knownUserIds = new Set(
      (options?.teams ?? []).flatMap((team) =>
        team.users.map((user) => user.id)
      )
    );
    const unavailableUsers = (ownership.assignment?.selections ?? [])
      .filter(
        (selection) =>
          selection.type === 'user' &&
          selection.id &&
          !knownUserIds.has(selection.id)
      )
      .map((selection) => ({
        id: selection.id!,
        name: `${selection.label} (unavailable)`,
        disabled: true,
        disabledReason: 'This user is no longer an active assignment option',
      }));
    return [...roles, ...unavailableUsers];
  }, [options, ownership.assignment?.selections]);
  const roleKeys = useMemo(
    () => new Set((options?.roles ?? []).map((role) => role.key)),
    [options]
  );
  const hasSelection = selection.teamIds.length + selection.userIds.length > 0;
  const hadAssignment = (ownership.assignment?.selections.length ?? 0) > 0;
  const selectedOwnerLabel = useMemo(() => {
    const initial = initialSelection(ownership);
    const assignedOwners = ownership.assignment?.owners ?? [];
    if (assignedOwners.length > 0 && selectionsMatch(selection, initial)) {
      return `${assignedOwners[0].name}${
        assignedOwners.length > 1 ? ` +${assignedOwners.length - 1}` : ''
      }`;
    }

    const fallbackLabels = new Map<string, string>();
    for (const current of ownership.assignment?.selections ?? []) {
      if (current.id) fallbackLabels.set(current.id, current.label);
      if (current.key) fallbackLabels.set(current.key, current.label);
    }

    const labels = [
      ...selection.teamIds.map(
        (id) =>
          teamsData.find((team) => team.teamId === id)?.teamName ??
          fallbackLabels.get(id) ??
          id
      ),
      ...selection.userIds.map((id) => {
        const role = roleOptions.find((option) => option.id === id);
        if (role) return role.name;
        const user = teamsData
          .flatMap((team) => team.users)
          .find((candidate) => candidate.id === id);
        return user?.name ?? user?.email ?? fallbackLabels.get(id) ?? id;
      }),
    ];
    if (labels.length === 0) return 'Select owners';
    return `${labels[0]}${labels.length > 1 ? ` +${labels.length - 1}` : ''}`;
  }, [ownership, roleOptions, selection, teamsData]);

  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Clicks inside the owner picker, the link dialog or the @mention list are
  // not "outside", even though those layers render outside the card.
  const outsideClickBlockedRef = useRef(false);
  outsideClickBlockedRef.current = saving || pickerOpen || linkModalOpen;

  useEffect(() => {
    // Clicking outside the card cancels, like the Cancel button.
    const onPointerDown = (event: MouseEvent) => {
      if (outsideClickBlockedRef.current) return;
      const target = event.target as HTMLElement | null;
      if (rootRef.current?.contains(target)) return;
      if (target?.closest?.(FLOATING_LAYER_SELECTOR)) return;
      onCancelRef.current();
    };
    // Capture phase on window: runs before the picker's own click-away handler
    // closes it, so an open picker still counts as open for this click.
    window.addEventListener('mousedown', onPointerDown, true);
    return () => window.removeEventListener('mousedown', onPointerDown, true);
  }, []);

  // Give focus back to whatever opened the panel once it closes.
  useEffect(() => {
    if (isAssign) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    return () => opener?.focus?.();
  }, [isAssign]);

  const commentTooLong = commentLength > ALERT_COMMENT_MAX_LENGTH;

  const handleCommentChange = (empty: boolean) => {
    setCommentLength(
      empty ? 0 : getCommentTextLength(editorRef.current?.getHTML() ?? '')
    );
  };

  // Put the caret in the comment box, as in the design.
  useEffect(() => {
    if (isAssign) return undefined;
    const frame = requestAnimationFrame(() => editorRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isAssign]);

  const submitClose = () => {
    if (!onSubmitClose || (tagRequired && !selectedTagId) || commentTooLong) {
      return;
    }
    const comment = editorRef.current?.isEmpty()
      ? ''
      : (editorRef.current?.getHTML() ?? '');
    setSaving(true);
    onSubmitClose({ tagId: selectedTagId, comment })
      .then(() => onCommitted())
      .catch((error) => {
        message.error(getAlertCloseErrorMessage(error));
        onUnauthorized(error);
      })
      .finally(() => setSaving(false));
  };

  const submit = () => {
    if (!isAssign) {
      submitClose();
      return;
    }
    if (commentTooLong) return;
    const comment = editorRef.current?.isEmpty()
      ? ''
      : (editorRef.current?.getHTML() ?? '');
    const attachmentFiles = selectedFiles
      .map(
        (file) =>
          file.originFileObj ??
          // beforeUpload returns false, so antd keeps the picked File itself in
          // the list with no originFileObj: the item is the RcFile.
          (file instanceof File
            ? (file as NonNullable<UploadFile['originFileObj']>)
            : undefined)
      )
      .filter(
        (file): file is NonNullable<UploadFile['originFileObj']> => file != null
      );
    setSaving(true);
    AssignShipmentAlert(
      token,
      alertId,
      {
        expected_version: ownership.version ?? 0,
        client_action_id: crypto.randomUUID(),
        tag_id: selectedTagId,
        comment,
        selections: [
          ...selection.teamIds.map((id) => ({ type: 'team' as const, id })),
          ...selection.userIds.map((id) =>
            roleKeys.has(id)
              ? { type: 'role' as const, key: id }
              : { type: 'user' as const, id }
          ),
        ],
      },
      attachmentFiles
    )
      .then(() => {
        message.success(hasSelection ? 'Alert assigned.' : 'Alert unassigned.');
        onCommitted();
      })
      .catch((error) => {
        const status = error?.response?.status ?? error?.status;
        // The shared interceptor already toasts the server's message for any
        // HTTP error; a second toast here would show it twice.
        if (!status) message.error('Unable to update alert assignment.');
        if (status === 409) {
          onCommitted();
          return;
        }
        onUnauthorized(error);
      })
      .finally(() => setSaving(false));
  };

  // A required tag that has not been picked yet blocks the primary action.
  const needsTag = tagRequired && !selectedTagId;
  const primaryDisabled =
    loading || (isAssign && !hasSelection && !hadAssignment) || needsTag;

  const selectedIndex = tags.findIndex((tag) => tag.id === selectedTagId);
  const tabStopIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const toggleTag = (id: string) =>
    setSelectedTagId((current) => (current === id ? null : id));

  // Radio-group keys: Enter / Space toggle, arrows move the selection.
  const handleTagKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    id: string,
    index: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleTag(id);
      return;
    }
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!step) return;
    event.preventDefault();
    const next = (index + step + tags.length) % tags.length;
    setSelectedTagId(tags[next].id);
    (
      event.currentTarget.parentElement?.children[next] as
        HTMLElement | undefined
    )?.focus();
  };

  return (
    <Flex
      ref={rootRef}
      vertical
      gap={12}
      onKeyDown={(event) => {
        if (!isAssign && event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
      style={{
        background: 'var(--surface-container)',
        border: '2px solid var(--text-primary)',
        borderRadius: 8,
        padding: 12,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {tags.length > 0 && (
        <Flex vertical gap={6}>
          <Flex
            gap={6}
            wrap
            role='radiogroup'
            aria-label={TAG_GROUP_LABEL[mode]}
            aria-required={tagRequired || undefined}
          >
            {tags.map((tag, index) => (
              <Tag
                key={tag.id}
                variant='filled'
                role='radio'
                aria-checked={selectedTagId === tag.id}
                tabIndex={index === tabStopIndex ? 0 : -1}
                onClick={() => toggleTag(tag.id)}
                onKeyDown={(event) => handleTagKeyDown(event, tag.id, index)}
                style={{
                  marginInlineEnd: 0,
                  background:
                    selectedTagId === tag.id
                      ? 'var(--text-primary)'
                      : 'var(--light-gray)',
                  color:
                    selectedTagId === tag.id
                      ? 'var(--surface-container)'
                      : 'var(--text-primary)',
                  cursor: 'pointer',
                  borderRadius: 999,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: '16px',
                }}
              >
                {tag.label}
              </Tag>
            ))}
          </Flex>
        </Flex>
      )}

      {tags.length > 0 && (
        <svg
          role='separator'
          aria-orientation='horizontal'
          width='456'
          height='1'
          viewBox='0 0 456 1'
          preserveAspectRatio='none'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className={styles.tagSeparator}
        >
          <line y1='0.5' x2='456' y2='0.5' stroke='#D9D9D9' />
        </svg>
      )}

      <div className={styles.commentEditor}>
        <RichEditor
          ref={editorRef}
          mentionRecipients={mentionRecipients}
          userColorMap={userColorMap}
          placeholder='Add comment (optional)'
          currentAudienceType={1}
          onSearchMentions={searchMentions}
          onEmptyChange={handleCommentChange}
        />
      </div>
      {commentTooLong && (
        <span role='alert' className={styles.fieldError}>
          {COMMENT_TOO_LONG_MESSAGE}
        </span>
      )}

      {isAssign && selectedFiles.length > 0 && (
        <Flex gap={4} wrap aria-label='Selected attachments'>
          {selectedFiles.map((file) => (
            <Tag key={file.uid} className={styles.fileTag}>
              <Tooltip title={file.name}>
                {file.name.length > 15
                  ? `${file.name.slice(0, 15)}...`
                  : file.name}
              </Tooltip>
              <DeleteIcon
                color='currentColor'
                role='button'
                aria-label={`Remove ${file.name}`}
                onClick={() =>
                  setSelectedFiles((current) =>
                    current.filter((candidate) => candidate.uid !== file.uid)
                  )
                }
              />
            </Tag>
          ))}
        </Flex>
      )}

      {/* The footer is one line: the owner picker and icons on the left, Cancel
          and the action on the right. Only when the card is narrower than
          350px do they wrap to a second line (see the stylesheet). */}
      <div className={styles.footerWrap}>
        <Flex
          className={styles.footer}
          justify={isAssign ? 'space-between' : 'flex-end'}
          align='center'
          wrap
        >
          {isAssign && (
            <Flex gap={4} align='center' wrap className={styles.footerStart}>
              <Popover
                trigger='click'
                placement='bottomLeft'
                autoAdjustOverflow={false}
                // The picker never flips up, so on short viewports it overflows
                // the bottom edge. Mount it in the drawer's scroll container so
                // that overflow scrolls the drawer instead of growing the page,
                // which toggled a window scrollbar and made the drawer jitter.
                getPopupContainer={(trigger) =>
                  trigger.closest<HTMLElement>('.custom-card-drawer') ??
                  document.body
                }
                arrow={false}
                open={pickerOpen}
                onOpenChange={(open) => {
                  if (!loading && !saving) setPickerOpen(open);
                }}
                styles={{
                  container: { padding: 0 },
                  content: { padding: 0 },
                }}
                content={
                  <div className={styles.pickerPopover}>
                    <TeamUserSelect
                      teamsData={teamsData}
                      additionalOptions={roleOptions}
                      value={selection}
                      onChange={setSelection}
                      disabled={loading || saving}
                      isTeamSelectAllDisabled={(teamId) =>
                        options?.teams.find((team) => team.id === teamId)
                          ?.disabled ?? true
                      }
                      placeholder='Select owners'
                      openInline
                    />
                  </div>
                }
              >
                <Button
                  type='text'
                  disabled={loading || saving}
                  aria-label='Choose alert owners'
                  className={styles.ownerTrigger}
                >
                  <span aria-hidden>@</span>
                  <span className={styles.ownerTriggerLabel}>
                    {loading ? 'Loading owners…' : selectedOwnerLabel}
                  </span>
                  <DownOutlined
                    aria-hidden
                    className={styles.ownerTriggerChevron}
                  />
                </Button>
              </Popover>
              <Upload
                beforeUpload={() => false}
                showUploadList={false}
                multiple={false}
                onChange={handleFileChange}
              >
                <Button
                  type='text'
                  aria-label='Attach file'
                  disabled={saving}
                  icon={<FooterIcon name='attachment' />}
                />
              </Upload>
              <Button
                type='text'
                aria-label='Mention owner'
                disabled={saving}
                onClick={() => editorRef.current?.insertAtSymbol()}
                icon={<FooterIcon name='mention' />}
              />
              <Button
                type='text'
                aria-label='Add link'
                disabled={saving}
                onClick={openLinkModal}
                icon={<FooterIcon name='link' />}
              />
            </Flex>
          )}
          <Flex gap={8} className={styles.footerActions}>
            <Button
              type='text'
              onClick={onCancel}
              disabled={saving}
              style={{ fontSize: 12 }}
            >
              Cancel
            </Button>
            {/* A disabled button swallows hover, so the tooltip sits on a wrapper. */}
            <Tooltip
              title={needsTag ? TAG_REQUIRED_MESSAGE : undefined}
              styles={{ container: { fontSize: 10 } }}
            >
              <span
                className={
                  primaryDisabled
                    ? `${styles.primaryActionWrap} ${styles.primaryActionWrapDisabled}`
                    : styles.primaryActionWrap
                }
              >
                <Button
                  type='primary'
                  loading={saving}
                  disabled={primaryDisabled}
                  onClick={submit}
                  style={{
                    // Disabled stays readable instead of fading out.
                    background: primaryDisabled
                      ? 'var(--light-gray)'
                      : 'var(--text-primary)',
                    color: primaryDisabled
                      ? 'var(--text-tertiary)'
                      : 'var(--surface-container)',
                    border: 0,
                    borderRadius: 8,
                    boxShadow: 'none',
                    fontSize: 12,
                  }}
                >
                  {isAssign
                    ? hasSelection || !hadAssignment
                      ? 'Assign'
                      : 'Unassign'
                    : PRIMARY_LABEL[mode]}
                </Button>
              </span>
            </Tooltip>
          </Flex>
        </Flex>
      </div>

      {isAssign && (
        <AddLinkForm
          editorRef={editorRef}
          setVisible={setLinkModalOpen}
          visible={linkModalOpen}
          setLinkText={setLinkText}
          linkText={linkText}
        />
      )}
    </Flex>
  );
};

export default AlertAssignmentPanel;
